import React, { useMemo, useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useUiContext } from '../../../../../../context/UiContext';
import BillService from '../../../../../../services/BillService';

function hasLineItemWithAmount(bill) {
  return !!(
    bill?._bill_line_items?.length &&
    bill?._bill_line_items.some((item) => item.amount && item.amount > 0)
  );
}

function createPayNowActiveDescription(bill, loan) {
  if (bill.payee_type === 'borrower' && loan.ach_type !== 'off') {
    return 'Borrower will be paid via ACH. A bill payment will be created, applied to this bill, and ACH sent through Bill.com.';
  }

  if (bill.payee_type === 'borrower' && loan.ach_type === 'off') {
    return "Borrower will be paid via check mailed to borrower's billing address. A bill payment will be created, applied to this bill, and check mailed through Lob.";
  }

  return bill._vendor?._bank_account
    ? `${
        bill._vendor?.name || 'Vendor'
      } will be paid via ACH to their account at ${
        bill._vendor?._bank_account?.bank_name || 'bank'
      }. A bill payment will be created, applied to this bill, and ACH initiated.`
    : `${
        bill._vendor?.name || 'Vendor'
      } will be paid via check mailed form Bill.com to ${
        bill?._vendor?.address_line_1 || "vendor's address"
      }. A bill payment will be created, applied to this bill, and check mailed through Bill.com.`;
}

function createConfirmationMessage(bill) {
  if (bill.payee_type === 'vendor' && bill._vendor?._bank_account) {
    return `${bill._vendor?.name} will be paid via ACH to their account at ${bill._vendor?._bank_account?.bank_name}. Are you sure you want to proceed?`;
  }

  if (bill.payee_type === 'vendor' && !bill._vendor?._bank_account) {
    return `${bill._vendor?.name} will be paid via an automated check mailed to their ${bill._vendor?.address_line_1} address. Are you sure you want to proceed?`;
  }

  if (bill.payee_type === 'lender') {
    return `${bill._lender?.name} will be paid via ACH. Are you sure you want to proceed?`;
  }

  return 'Are you sure you want to proceed?';
}

export default function useBillActions({
  bill,
  isLender,
  refetchLoan,
  loanId,
  refetch,
  loan,
}) {
  const { showDialogModal, showToast } = useUiContext();

  const navigate = useNavigate();

  const isMarkPaidButtonEnabled =
    bill?.status !== 'paid' && bill?.status !== 'partial';
  const canPayNow =
    bill?.status === 'active' &&
    (bill?.type === 'Insurance' ||
      bill?.type === 'Claim Distribution' ||
      bill?.type === 'Refund' ||
      bill?.type === 'Impound Refund') &&
    hasLineItemWithAmount(bill) &&
    !isLender;
  const isTaxBill = bill?.type === 'Tax';
  const canBeDeleted =
    bill?.status !== 'paid' && bill?.status !== 'partial' && !isLender;

  const deleteBillWithConfirmation = useCallback(() => {
    showDialogModal({
      title: 'Confirmation',
      type: 'danger',
      message: 'Are you sure you want to delete this bill?',
      onConfirm: async () => {
        try {
          await BillService.deleteBill(bill?.id);
          showToast({
            text: 'Bill deleted successfully',
            type: 'success',
          });
          await refetchLoan();
          navigate(`/loans/${loanId}?tab=Bills`);
        } catch (error) {
          console.log(error);
          showToast({
            text: error?.response?.data?.message || 'Something went wrong',
            type: 'error',
          });
        }
      },
    });
  }, [bill, loanId, navigate, refetchLoan, showDialogModal, showToast]);

  const { mutate: markBillPaid } = useMutation({
    mutationFn: () => BillService.payBillFromOutside(bill.id),
    onSuccess: () => {
      showToast({
        text: 'The bill has been marked as paid',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: setupForNextYear } = useMutation({
    mutationFn: () => BillService.setupBillForNextYear(bill?.id),
    onSuccess: () => {
      showToast({
        text: 'Bill successfully setup for next year',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: payBillNow } = useMutation({
    mutationFn: () => BillService.payBillNow(bill.id),
    onSuccess: () => {
      showToast({
        text: 'Bill payed successfully',
        type: 'success',
      });
      refetch();
      refetchLoan();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const actions = useMemo(() => {
    if (!bill) {
      return [];
    }
    // eslint-disable-next-line no-shadow
    const actions = [
      {
        actionTitle: 'Pay this bill',
        actionDescription: canPayNow
          ? createPayNowActiveDescription(bill, loan)
          : 'This bill cannot be paid until it is made active.',
        disabled: !canPayNow,
        onClick: () => {
          showDialogModal({
            title: 'Confirmation',
            message: createConfirmationMessage(bill),
            onConfirm: () => {
              payBillNow();
            },
            confirmButtonLabel: 'Yes, proceed',
            cancelButtonLabel: 'No, cancel',
          });
        },
      },
      {
        actionTitle: 'Mark as paid from outside',
        actionDescription: isMarkPaidButtonEnabled
          ? "This bill will be marked as having been paid from somewhere other than the loan's impound account."
          : 'This bill is already paid. If that done incorrectly, the bill payment needs to be reversed.',
        disabled: !isMarkPaidButtonEnabled,
        onClick: () => {
          showDialogModal({
            title: 'Confirmation',
            message:
              'Are you sure you want to mark this bill as having been paid by a 3rd party? This will exclude the bill from the impound schedule',
            onConfirm: () => {
              markBillPaid();
            },
            confirmButtonLabel: 'Yes, mark as paid',
            cancelButtonLabel: 'No, cancel',
          });
        },
      },
      {
        actionTitle: 'Setup for next year',
        actionDescription: isTaxBill
          ? "A bill will be created for the same amount dated one year from this bill's due date."
          : 'The tax bill cannot be setup for next year until it is paid.',
        disabled: !isTaxBill,
        onClick: setupForNextYear,
      },
      {
        actionTitle: 'Delete bill',
        actionDescription: canBeDeleted
          ? 'This will permanently delete the bill.'
          : 'This bill cannot be deleted because it is already paid. The bill payment must first be reversed.',
        disabled: !canBeDeleted,
        icon: (
          <span className="text-red-500">
            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
              <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
              <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
            </svg>
          </span>
        ),
        onClick: deleteBillWithConfirmation,
      },
    ]; /* .sort((a, b) => a.disabled - b.disabled) */

    return actions;
  }, [
    bill,
    canPayNow,
    loan,
    isMarkPaidButtonEnabled,
    isTaxBill,
    setupForNextYear,
    canBeDeleted,
    deleteBillWithConfirmation,
    showDialogModal,
    payBillNow,
    markBillPaid,
  ]);

  return actions;
}
